import { getAramShopCatalog } from '../../domain/items/aramShopCatalog.js';
import {
  ARAM_SHOP_CATEGORIES,
  filterAramShopCatalog
} from '../../domain/items/aramShopFilter.js';
import { useInventoryItem } from '../../application/useInventoryItem.js';

export class ShopUI {
  constructor({
    shopModal,
    shopItemsList,
    shopSearchInput,
    shopCategoryRoot,
    inventorySlots,
    playerInventory,
    championEntity,
    getGameTime,
    globalObj = typeof window !== 'undefined' ? window : null
  }) {
    this.shopModal = shopModal;
    this.shopItemsList = shopItemsList;
    this.shopSearchInput = shopSearchInput || null;
    this.shopCategoryRoot = shopCategoryRoot || null;
    this.inventorySlots = inventorySlots;
    this.playerInventory = playerInventory;
    this.championEntity = championEntity;
    this.getGameTime = typeof getGameTime === 'function' ? getGameTime : () => performance.now() / 1000;
    this.searchQuery = '';
    this.category = 'all';
    this.bindSearchInput();
    this.bindCategoryTabs();
    this.wireDebug(globalObj);
    this.initInventorySlotListeners();
  }

  bindSearchInput() {
    if (!this.shopSearchInput) return;
    this.shopSearchInput.addEventListener('input', () => {
      this.searchQuery = this.shopSearchInput.value || '';
      if (this.isOpen()) this.updateShopUI();
    });
  }

  isOpen() {
    return this.shopModal.style.display === 'flex';
  }

  toggleShop() {
    const isVisible = this.isOpen();
    this.shopModal.style.display = isVisible ? 'none' : 'flex';
    if (!isVisible) this.updateShopUI();
  }

  bindCategoryTabs() {
    if (!this.shopCategoryRoot) return;
    this.shopCategoryRoot.addEventListener('click', (ev) => {
      const btn = ev.target?.closest?.('[data-shop-category]');
      if (!btn) return;
      this.setCategory(btn.getAttribute('data-shop-category'));
    });
  }

  wireDebug(globalObj) {
    if (!globalObj) return;
    globalObj.__debugShopCategory = (cat) => (
      cat === undefined ? this.category : this.setCategory(cat)
    );
    globalObj.__debugUndoLastBuy = () => this.playerInventory?.undoLastBuy?.();
    globalObj.__debugItemActiveCd = (slotIndex) => (
      this.playerInventory?.getActiveCooldownRemaining?.(
        slotIndex,
        this.getGameTime()
      ) ?? 0
    );
  }

  setCategory(category) {
    if (!ARAM_SHOP_CATEGORIES.includes(category)) return this.category;
    this.category = category;
    if (this.isOpen()) this.updateShopUI();
    return this.category;
  }

  visibleCatalog() {
    return filterAramShopCatalog(getAramShopCatalog(), this.searchQuery, this.category);
  }

  updateShopUI() {
    this.shopItemsList.innerHTML = '';
    Object.values(this.visibleCatalog()).forEach((item) => {
      this.shopItemsList.appendChild(this.buildShopRow(item));
    });
  }

  buildShopRow(item) {
    const craftingCost = this.playerInventory.getItemCraftingCost(item.id);
    const canAfford = this.championEntity.gold >= craftingCost;
    const isDiscounted = craftingCost < item.cost;
    const row = document.createElement('div');
    row.className = 'shop-item-row';
    row.innerHTML = `
        <div class="shop-item-info">
          <span class="shop-item-icon">${item.icon}</span>
          <div class="shop-item-details">
            <span class="shop-item-name">${item.name} (${item.cost}g)</span>
            <span class="shop-item-desc">${item.description}</span>
          </div>
        </div>
        <button class="shop-buy-btn" ${canAfford ? '' : 'disabled'}>
          ${isDiscounted ? `Combinar (${craftingCost}g)` : `Comprar (${item.cost}g)`}
        </button>
      `;
    row.querySelector('.shop-buy-btn').addEventListener('click', () => {
      const res = this.playerInventory.buyItem(item.id);
      if (res.success) {
        this.updateShopUI();
        this.updateInventoryUI();
      }
    });
    return row;
  }

  updateInventoryUI() {
    this.inventorySlots.forEach((slotEl, idx) => {
      const item = this.playerInventory.slots[idx];
      if (item) {
        slotEl.textContent = item.icon;
        const useHint = item.isConsumable || item.hasActive
          ? ' / Clic para Usar'
          : '';
        slotEl.title = `${item.name}\n${item.description}\n(Clic para Vender por ${Math.floor(item.cost * 0.7)}g${useHint})`;
      } else {
        slotEl.textContent = '';
        slotEl.title = `Ranura ${idx + 1} vacía`;
      }
    });
  }

  initInventorySlotListeners() {
    this.inventorySlots.forEach((slotEl, idx) => {
      slotEl.addEventListener('click', () => {
        const item = this.playerInventory.slots[idx];
        if (item) {
          const dead = !!this.championEntity.isDead;
          if ((item.isConsumable || item.hasActive) && !dead) {
            useInventoryItem(this.playerInventory, idx, { now: this.getGameTime() });
          } else {
            this.playerInventory.sellItem(idx);
          }
          this.updateShopUI();
          this.updateInventoryUI();
        }
      });
    });
  }
}
